// Recarrega o app quando um import() de tela falha porque o chunk hasheado sumiu
// após um deploy (aba aberta antes da nova versão). Em vez do crash "Failed to
// fetch dynamically imported module", mostra "Atualizando…" e dá reload.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorComponent, useRouterState } from "@tanstack/react-router";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Logo } from "../brand.js";
import {
  RELOAD_GUARD_KEY,
  RELOAD_WINDOW_MS,
  VERSION_RELOAD_GUARD_KEY,
  clearCacheAndRestart,
  isChunkLoadError,
  readReloadGuard,
  shouldReload,
  writeReloadGuard,
} from "./app-recovery.js";
import { AppErrorFallback } from "./app-error-boundary.js";
import {
  CONTROLLER_WAIT_MS,
  applyUpdate,
  checkForSwUpdate,
  hasServiceWorkerControl,
  notifyUpdateReady,
  onUpdateReady,
} from "./app-sw-update.js";
import { BUILD_ID, fetchDeployedBuildId } from "./app-version.js";
import { isCallWorkspacePath } from "./call-workspace.js";
import "../i18n.js";

export { RELOAD_GUARD_KEY, RELOAD_WINDOW_MS, isChunkLoadError, shouldReload };
// Depois que a pessoa escolhe atualizar, deixa a tela de progresso aparecer
// antes do reload. A detecção sozinha nunca inicia este atraso.
export const RELOAD_DELAY_MS = 1000;
// Teto para a atualização inteira. Derivado do caminho real, não de um número
// redondo: RELOAD_DELAY_MS até chamar `applyUpdate` + CONTROLLER_WAIT_MS que ele
// espera o SW novo assumir a aba, e uma folga. Passou disso sem o reload ter
// acontecido, é porque `applyUpdate` está pendurado (registro do SW que nunca
// resolve) — a tela vira a variante "failed" em vez de deixar o vendedor preso
// num spinner sem saída. Um teto muito maior que o caminho real seria decorativo:
// nunca dispararia.
export const UPDATE_TIMEOUT_MS = RELOAD_DELAY_MS + CONTROLLER_WAIT_MS + 2000;
export const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
export const VERSION_CHECK_MIN_GAP_MS = 30 * 1000;

// Overlay de tela cheia. `updating` = caminho feliz (spinner + reload em curso).
// `failed` = guarda anti-loop acionada (reload já falhou); oferece retry manual.
function UpdatingScreen({
  variant,
  onRetry,
  // O botão secundário some quando o próprio retry já limpa o cache — dois
  // botões com rótulos diferentes para a mesma ação só confundem.
  withClearCache = true,
}: {
  variant: "updating" | "failed";
  onRetry: () => void;
  withClearCache?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-background px-6 text-center text-foreground"
    >
      {variant === "updating" ? (
        <>
          <Logo className="size-20 animate-pulse motion-reduce:animate-none" />
          <p className="text-sm text-muted-foreground">{t("appUpdater.updating")}</p>
        </>
      ) : (
        <>
          <p className="text-base font-medium">{t("appUpdater.failed")}</p>
          <p className="max-w-xs text-sm text-muted-foreground">
            {t("appUpdater.checkConnection")}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button onClick={onRetry}>{t("appUpdater.retry")}</Button>
            {withClearCache ? (
              <Button variant="outline" onClick={() => void clearCacheAndRestart()}>
                {t("appUpdater.clearCache")}
              </Button>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

// Aviso persistente, mas não bloqueante: uma versão nova não interrompe o que
// a pessoa está fazendo. O card só desaparece quando ela decide atualizar e o
// fluxo de progresso assume a tela.
function UpdateAvailableCard({ inCall, onUpdate }: { inCall: boolean; onUpdate: () => void }) {
  const { t } = useTranslation();
  return (
    <Alert
      role="status"
      aria-live="polite"
      className="fixed top-4 left-1/2 z-50 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 border-border/80 bg-card/95 p-4 shadow-lg shadow-black/10 backdrop-blur-md"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span className="grid size-9 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
            <RefreshCw aria-hidden="true" className="size-4" />
          </span>
          <div className="min-w-0">
            <AlertTitle>{t("appUpdater.available")}</AlertTitle>
            <AlertDescription className="mt-0.5">
              {t(inCall ? "appUpdater.duringCall" : "appUpdater.availableDescription")}
            </AlertDescription>
          </div>
        </div>
        <Button type="button" size="sm" className="shrink-0" onClick={onUpdate}>
          {t("appUpdater.updateNow")}
        </Button>
      </div>
    </Alert>
  );
}

// Error component do router (registrado como defaultErrorComponent). É AQUI que
// um import() de tela lazy que falhou — chunk hasheado que sumiu após um deploy
// — chega de forma determinística: o CatchBoundary global do TanStack Router
// fica DENTRO do RouterProvider, então um error boundary externo nunca veria
// esse erro. Em produção, erro de chunk → tela "Atualizando…" + reload (guardado
// contra loop). Qualquer outro erro (e tudo em dev) cai no ErrorComponent padrão
// do router — mesmo comportamento de antes.
export function AppErrorScreen({ error }: { error: unknown }) {
  const isUpdate = import.meta.env.PROD && isChunkLoadError(error);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!isUpdate) return;
    const now = Date.now();
    if (shouldReload(now, readReloadGuard(RELOAD_GUARD_KEY))) {
      writeReloadGuard(RELOAD_GUARD_KEY, now);
      const timer = setTimeout(() => location.reload(), RELOAD_DELAY_MS);
      return () => clearTimeout(timer); // limpa se desmontar antes do reload
    }
    setFailed(true); // reload já falhou dentro da janela: para de loopar
  }, [isUpdate]);

  const retry = () => {
    writeReloadGuard(RELOAD_GUARD_KEY, Date.now());
    location.reload();
  };

  if (isUpdate) {
    return <UpdatingScreen variant={failed ? "failed" : "updating"} onRetry={retry} />;
  }
  // Em dev, o ErrorComponent do router (stack trace) é mais útil. Em produção,
  // a tela padrão do app — com a saída "limpar cache e reiniciar".
  if (import.meta.env.DEV) return <ErrorComponent error={error} />;
  return <AppErrorFallback error={error} />;
}

// Montado na raiz do router: quando o SW avisa que há build nova, mantém o app
// em uso e oferece a atualização num card flutuante. Só o clique da pessoa
// ativa o SW novo e recarrega a aba. Em rotas de ligação, o card deixa explícito
// que atualizar naquele momento interrompe o atendimento.
//
// A guarda anti-loop continua necessária: se o `skipWaiting` não pegar, o SW
// novo continua em `waiting`,
//    o `onNeedRefresh` chega de novo depois do reload e o app entraria num
//    ciclo de reload a cada poucos segundos. A guarda em sessionStorage
//    (VERSION_RELOAD_GUARD_KEY) corta esse ciclo na segunda passada e mostra a
//    variante "failed" — a mesma defesa que o AppErrorScreen faz para o chunk
//    faltante, com chave própria para não misturar os dois caminhos.
export function AppUpdateWatcher() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [ready, setReady] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => onUpdateReady(() => setReady(true)), []);

  // O SW pode levar horas para ser consultado novamente numa SPA. O JSON é uma
  // sondagem barata, mas não anuncia atualização antes do precache estar pronto
  // quando esta aba já é controlada por um SW.
  useEffect(() => {
    let cancelled = false;
    let lastCheck = 0;
    const controller = new AbortController();
    const check = async (force = false) => {
      if (cancelled || document.visibilityState !== "visible") return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      const now = Date.now();
      if (!force && now - lastCheck < VERSION_CHECK_MIN_GAP_MS) return;
      lastCheck = now;
      const deployed = await fetchDeployedBuildId(controller.signal);
      if (cancelled || deployed === null || deployed === BUILD_ID) return;
      if (hasServiceWorkerControl()) checkForSwUpdate();
      else notifyUpdateReady();
    };
    const onVisible = () => void check(true);
    const onOnline = () => void check(true);
    void check(true);
    const interval = window.setInterval(() => void check(true), VERSION_CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    window.addEventListener("online", onOnline);
    return () => {
      cancelled = true;
      controller.abort();
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("online", onOnline);
    };
  }, []);

  // O Vite dispara este evento antes de uma rejeição de import em alguns
  // navegadores. Transformamos ambos em um único sinal idempotente.
  useEffect(() => {
    const onPreloadError = (event: Event) => {
      event.preventDefault();
      notifyUpdateReady();
    };
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isChunkLoadError(event.reason)) notifyUpdateReady();
    };
    window.addEventListener("vite:preloadError", onPreloadError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      window.removeEventListener("vite:preloadError", onPreloadError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  const inCall = isCallWorkspacePath(pathname);

  const startUpdate = () => {
    const now = Date.now();
    setUpdating(true);
    if (!shouldReload(now, readReloadGuard(VERSION_RELOAD_GUARD_KEY))) {
      setFailed(true);
      return;
    }
    writeReloadGuard(VERSION_RELOAD_GUARD_KEY, now);
  };

  useEffect(() => {
    if (!updating || failed) return;
    const apply = setTimeout(() => void applyUpdate(), RELOAD_DELAY_MS);
    // Rede de segurança: se o reload não veio dentro do teto, oferece saída
    // manual em vez de deixar o spinner girando para sempre.
    const bail = setTimeout(() => setFailed(true), UPDATE_TIMEOUT_MS);
    return () => {
      clearTimeout(apply);
      clearTimeout(bail);
    };
  }, [updating, failed]);

  if (!ready) return null;
  if (!updating) return <UpdateAvailableCard inCall={inCall} onUpdate={startUpdate} />;
  // O retry NÃO pode ser `location.reload()` puro: chegar aqui significa que o
  // SW novo justamente NÃO assumiu a aba, e numa aba controlada pelo SW velho o
  // reload é respondido pelo index.html do precache VELHO — o mesmo estado, de
  // novo. `clearCacheAndRestart` apaga o Cache Storage e desregistra o SW antes
  // de recarregar, que é o único caminho que realmente sai daqui.
  return (
    <UpdatingScreen
      variant={failed ? "failed" : "updating"}
      onRetry={() => void clearCacheAndRestart()}
      withClearCache={false}
    />
  );
}
